'use client';

import { CirclePlusIcon } from 'lucide-react';
import * as React from 'react';

import { getNodesByKeys, TreeCommand, TreeNodeData } from 'src/components/tree';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Command, CommandGroup, CommandItem, CommandSeparator } from 'src/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import { Separator } from 'src/components/ui/separator';
import { useControl } from 'src/hooks';
import { cn } from 'src/lib/utils';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export type SelectionType<TMultiple extends boolean> = TMultiple extends true
  ? string[]
  : string | null;

export interface TreeFilterProps<TData, TMultiple extends boolean = false> {
  title?: string;
  data: TreeNodeData<TData>[];
  multiple?: TMultiple;
  // TreeCommand 的其他属性
  defaultExpandAll?: boolean;
  defaultExpandedKeys?: string[];
  filterTreeNode?: (node: TreeNodeData<TData>, searchValue: string) => boolean;

  searchPlaceholder?: string;
  className?: string;

  defaultValue?: SelectionType<TMultiple>;
  value?: SelectionType<TMultiple>;
  onChange?: (value?: SelectionType<TMultiple>) => void;
}

export function TreeFilter<TData = unknown, TMultiple extends boolean = false>({
  title,
  data,
  multiple = false as TMultiple,
  defaultExpandAll = false,
  defaultExpandedKeys,
  filterTreeNode,
  searchPlaceholder = '搜索...',
  className,
  defaultValue,
  value,
  onChange,
}: TreeFilterProps<TData, TMultiple>) {
  const isMultiple = multiple;
  // filterValue 永远是数组，没有选中值就是空数组
  const [selection, setSelection] = useControl<SelectionType<TMultiple>>(
    (defaultValue || null) as SelectionType<TMultiple>,
    value,
    onChange
  );
  const [open, setOpen] = React.useState(false);

  const selectedValues = React.useMemo(() => {
    if (isMultiple) return (selection as string[]) ?? [];
    return selection ? [selection as string] : [];
  }, [isMultiple, selection]);

  // 获取选中的节点用于显示
  const selectedNodes = React.useMemo(() => {
    return getNodesByKeys(data, selectedValues);
  }, [data, selectedValues]);

  // 选中变化时立即触发过滤
  const handleSelectionChange = (value: string[] | string | null) => {
    setSelection(value as SelectionType<TMultiple>);
  };

  const handleReset = () => {
    setSelection(null as SelectionType<TMultiple>);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed px-2.5 ">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex gap-2 items-center h-full">
                <CirclePlusIcon className="h-4 w-4" />
                <div className="lg:flex items-center h-full hidden">
                  {title}
                  {selectedValues?.length > 0 && (
                    <>
                      <Separator orientation="vertical" className="mx-2 h-4 shrink-0" />
                      <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                        {selectedValues?.length}
                      </Badge>
                      <div className="hidden space-x-1 lg:flex">
                        {selectedValues.length > 2 ? (
                          <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                            {selectedValues.length} 项过滤
                          </Badge>
                        ) : (
                          selectedNodes.map((node) => (
                            <Badge
                              variant="secondary"
                              key={node.key}
                              className="rounded-sm px-1 font-normal"
                            >
                              <span className="max-w-18 overflow-hidden text-ellipsis whitespace-nowrap text-nowrap">
                                {node.name}
                              </span>
                            </Badge>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="flex lg:hidden items-center gap-1">
              {title}
              {selectedNodes.length > 0 && (
                <>
                  <Separator
                    orientation="vertical"
                    className="mx-1 data-[orientation=vertical]:h-2 shrink-0"
                  />
                  {selectedNodes.length > 2 ? (
                    <span>{selectedNodes.length} 项过滤</span>
                  ) : (
                    selectedNodes.map((node) => <span key={node.key}>{node.name}</span>)
                  )}
                </>
              )}
            </TooltipContent>
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-48 p-0', className)} align="start">
        <div className="flex flex-col max-h-[400px]">
          <div className="flex-1 overflow-hidden">
            <TreeCommand<TData, TMultiple>
              data={data}
              multiple={isMultiple}
              selection={selection}
              onSelectionChange={handleSelectionChange}
              defaultExpandAll={defaultExpandAll}
              defaultExpandedKeys={defaultExpandedKeys}
              filterTreeNode={filterTreeNode}
              searchPlaceholder={searchPlaceholder}
            />
          </div>
          <Command>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                disabled={selectedValues.length === 0}
                onSelect={handleReset}
                className="justify-center text-center cursor-pointer"
              >
                重置
              </CommandItem>
            </CommandGroup>
          </Command>
        </div>
      </PopoverContent>
    </Popover>
  );
}
